import { useCallback, useContext, useMemo } from "react"
import { GridInitialState } from "@mui/x-data-grid-premium"
import { DatagridPersistenceContext, type DatagridPersistenceState as DatagridState } from "./datagrid-persistence-context"

interface UseContextPersistenceReturn {
  isContextAvailable: boolean
  gridState: GridInitialState | null
  saveGridStateToContext: (state: GridInitialState) => boolean
}

export function useContextPersistence(gridId: string): UseContextPersistenceReturn {
  const source = useContext(DatagridPersistenceContext)
  const datagridState = source?.datagridState
  const setDatagridState = source?.setDatagridState

  const gridState = useMemo(() => {
    const currentDatagridState = datagridState
    if (!currentDatagridState) {
      return null
    }
    const baseState = currentDatagridState[gridId]
    if (!baseState) {
      return null
    }
    const shared = currentDatagridState.shared

    if (!shared) {
      return baseState
    }

    const currentDimensions = baseState.columns?.dimensions || {}
    const mergedDimensions = { ...currentDimensions }
    if (shared.labelDimensions !== undefined) {
      mergedDimensions.label = shared.labelDimensions
    }
    return {
      ...baseState,
      columns: {
        ...baseState.columns,
        dimensions: mergedDimensions,
      },
    }
  }, [datagridState, gridId])

  const saveGridStateToContext = useCallback((state: GridInitialState) => {
    if (!setDatagridState) {
      return false
    }
    const currentDatagridState = datagridState || {}

    const newDatagridState: DatagridState = { ...currentDatagridState, [gridId]: state }
    const labelDimensions = state.columns?.dimensions?.label
    if (labelDimensions) {
      newDatagridState.shared = {
        labelDimensions,
      }
    }
    setDatagridState(newDatagridState)
    return true
  }, [datagridState, setDatagridState, gridId])

  return {
    isContextAvailable: !!setDatagridState,
    gridState,
    saveGridStateToContext,
  }
}
